import ModalComponent from '@components/modal/Modal.component';
import {Box, Button, LinearProgress, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useState} from 'react';
import ClientsServicesTable from '@screens/admin/clients/services/ClientsServices.table';
import {clientsServicesQuery, clientsServicesService} from '@store/clients/services';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import {useSnackbar} from 'notistack';
import {LoadingButton} from '@mui/lab';
import * as io from 'socket.io-client';
import {fromEvent} from 'rxjs';
import env from '@config/env.config';

interface UpdateServicesPricesModalProps {
  handleClose: () => void;
}

const UpdateServicesPricesModal = (props: UpdateServicesPricesModalProps) => {

  const {handleClose} = props;
  const {t} = useTranslation();

  const {enqueueSnackbar} = useSnackbar();
  const [saveLoading, setSaveLoading] = useState(false);

  const [progress, setProgress] = useState<number>(0);

  const [step, setStep] = useState<number>(0);
  const [priceUpdate, setPriceUpdate] = useState<{
    unitCostInCents?: number;
    unitPriceInCents?: number;
    sellCoefficient?: number;
    buyCoefficient?: number;
    sellSum?: number;
    buySum?: number;
    sellSumInCents?: number;
    buySumInCents?: number;
    unitCost?: number;
    unitPrice?: number;
    validFromDate?: Date;
  }>({buyCoefficient: 1, sellCoefficient: 1});

  const errors = {
    buy: getFieldError(priceUpdate.buyCoefficient, FieldValidationType.REQUIRED_NUMBER) && getFieldError(priceUpdate.unitCost, FieldValidationType.REQUIRED_NUMBER) && getFieldError(priceUpdate.buySum, FieldValidationType.REQUIRED_NUMBER),
    sell: getFieldError(priceUpdate.sellCoefficient, FieldValidationType.REQUIRED_NUMBER) && getFieldError(priceUpdate.unitPrice, FieldValidationType.REQUIRED_NUMBER) && getFieldError(priceUpdate.sellSum, FieldValidationType.REQUIRED_NUMBER),
    date: getFieldError(priceUpdate.validFromDate, FieldValidationType.REQUIRED_DATE),
  };

  const handleCreateNewPrice = () => {
    setSaveLoading(true);
    const token = Math.random().toString(16).slice(2);
    const ioClient = io.connect(`${env.API_URL}${env.API_URL.endsWith('/') ? '' : '/'}ws`, {
      path: '/api/ws/socket.io'
    });
    clientsServicesService.updatePrices(priceUpdate, token).subscribe({
      next: () => {
        enqueueSnackbar(t('clients.details.contractedServices.updatePrices.success'), {variant: 'success'});
        handleClose();
        setSaveLoading(false);
        ioClient.disconnect();
      },
      error: (err) => {
        enqueueSnackbar(err.text, err.options);
        setSaveLoading(false);
      },
    });

    ioClient.on('connect', () => {
      fromEvent(ioClient, `progress-${token}`).subscribe((data: any) => {
        setProgress(data?.[0]);
      });
    });
  };


  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack pt="20px" pb="20px" height="85vh" width="100%" direction="column" spacing={2} alignItems="center">
            <Typography>
              {t('clients.details.contractedServices.updatePrices.step1Description')}
            </Typography>
            <Stack flex={1} width="100%" spacing={2}>
              <ClientsServicesTable enableActions={false}/>
            </Stack>
          </Stack>
        );
      case 1:
        return (
          <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={2} alignItems="center">
            <Stack flex={1} width="100%" spacing={5} alignItems="center">
              <Typography>
                {clientsServicesQuery.totalRows} {t('clients.details.contractedServices.updatePrices.selectedServices')}
              </Typography>
              <Stack direction="column" spacing={6}>
                <Stack spacing={2}>
                  <Typography variant="h5" sx={{textAlign: 'center'}}>
                    {t('clients.details.contractedServices.updatePrices.sell')}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.replace')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(unitPrice) => setPriceUpdate({
                          ...priceUpdate,
                          sellCoefficient: undefined,
                          sellSum: undefined,
                          sellSumInCents: undefined,
                          unitPrice: unitPrice,
                          unitPriceInCents: (unitPrice || 0) * 100,
                        })}
                        value={priceUpdate.unitPrice ?? undefined}
                        error={errors.sell}
                        required/>
                    </Box>
                    <Typography>•</Typography>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.multiply')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(sellCoeff) => setPriceUpdate({
                          ...priceUpdate,
                          unitPrice: undefined,
                          unitPriceInCents: undefined,
                          sellSum: undefined,
                          sellSumInCents: undefined,
                          sellCoefficient: sellCoeff,
                        })}
                        value={priceUpdate.sellCoefficient ?? undefined}
                        error={errors.sell}
                        required/>
                    </Box>
                    <Typography>•</Typography>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.add')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(sellSum) => setPriceUpdate({
                          ...priceUpdate,
                          sellCoefficient: undefined,
                          unitPrice: undefined,
                          unitPriceInCents: undefined,
                          sellSum: sellSum,
                          sellSumInCents: (sellSum || 0) * 100
                        })}
                        value={priceUpdate.sellSum ?? undefined}
                        error={errors.sell}
                        required/>
                    </Box>
                  </Stack>
                </Stack>
                <Stack spacing={2}>
                  <Typography variant="h5" sx={{textAlign: 'center'}}>
                    {t('clients.details.contractedServices.updatePrices.buy')}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.replace')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(unitCost) => setPriceUpdate({
                          ...priceUpdate,
                          unitCost: unitCost,
                          unitCostInCents: (unitCost || 0) * 100,
                          buySumInCents: undefined,
                          buySum: undefined,
                          buyCoefficient: undefined,
                        })}
                        value={priceUpdate.unitCost ?? undefined}
                        error={errors.buy}
                        required/>
                    </Box>
                    <Typography>•</Typography>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.multiply')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(buyCoeff) => setPriceUpdate({
                          ...priceUpdate,
                          buyCoefficient: buyCoeff,
                          unitCostInCents: undefined,
                          unitCost: undefined,
                          buySum: undefined,
                          buySumInCents: undefined,
                        })}
                        value={priceUpdate.buyCoefficient ?? undefined}
                        error={errors.buy}
                        required/>
                    </Box>
                    <Typography>•</Typography>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        color="secondary"
                        inputWidth={200}
                        labelWidth={50}
                        label={t('clients.details.contractedServices.updatePrices.add')}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(buySum) => setPriceUpdate({
                          ...priceUpdate,
                          unitCost: undefined,
                          unitCostInCents: undefined,
                          buyCoefficient: undefined,
                          buySum: buySum,
                          buySumInCents: (buySum || 0) * 100,
                        })}
                        value={priceUpdate.buySum ?? undefined}
                        error={errors.buy}
                        required/>
                    </Box>
                  </Stack>
                </Stack>
                <DatePickerComponent
                  color="secondary"
                  labelWidth={160}
                  label={t('clients.details.contractedServices.manage.validityPrice')}
                  value={priceUpdate.validFromDate}
                  inputWidth={120}
                  handleChange={(validFromDate) => {
                    setPriceUpdate({
                      ...priceUpdate,
                      validFromDate: validFromDate
                    });
                  }}
                  error={errors.date}
                  required
                />
              </Stack>
            </Stack>
          </Stack>
        );
      case 2:
        return (
          <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={3} alignItems="center">
            {progress === 0 &&
                <Typography>{t('clients.details.contractedServices.updatePrices.lastStepWarning', {count: clientsServicesQuery.totalRows})}</Typography>}
            {
              progress > 0 && <>
                    <Typography>{t('clients.details.contractedServices.updatePrices.updateInProgress')}</Typography>
                    <LinearProgress sx={{width: '100%'}} variant="determinate"
                                    value={progress / clientsServicesQuery.totalRows * 100}/>
                    <Typography>{progress} / {clientsServicesQuery.totalRows}</Typography>
                </>
            }

          </Stack>
        );
    }
  };

  const getStepAction = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Button variant="contained" color="primary" onClick={() => setStep(1)}>
            {t('global.next')}
          </Button>
        );
      case 1:
        return (
          <Button variant="contained" color="primary" disabled={checkIfErrors(errors)} onClick={() => setStep(2)}>
            {t('global.next')}
          </Button>
        );
      case 2:
        return (
          <LoadingButton loading={saveLoading} variant="contained" color="primary" onClick={handleCreateNewPrice}>
            {t('global.save')}
          </LoadingButton>
        );
    }
  };

  return (<ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    maxWidth={step === 0 ? false : 'lg'}
    title={t('clients.details.contractedServices.updatePrices.title')}
    content={
      getStepContent(step)
    }
    actions={(
      <Stack direction="row" spacing={2}>
        {step === 0 && <Button variant="outlined" color="primary" onClick={handleClose}>{t('global.cancel')}</Button>}
        {step !== 0 && <Button variant="outlined" color="primary"
                               onClick={() => setStep(step - 1)}>{t('global.previous')}</Button>}
        {getStepAction(step)}
      </Stack>
    )}
  />);
};

export default UpdateServicesPricesModal;
